import { Box, Button, Divider, Flex, Input } from '@byecode/ui'
import type { DataSourceAbstract, PageVariableSchema } from '@lighthouse/core'
import { SelectDataSource } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'

import type { PageVariableFormSlice } from '../../../formSlice'
import { ConditionConfigure } from './ConditionConfigure'
import { SortConfigure } from './SortConfigure'
import * as SC from './styles'
import { VariableList } from './VariableList'

const StyledForm = styled.form`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    font-size: 14px;
`

interface VariableFormProps {
    index: number
    dataSourceList: DataSourceAbstract[]

    defaultValues?: PageVariableSchema | null
    onCancel: () => void
    onConfirm: (data: PageVariableSchema) => void
}

const DEFAULT_VALUES: PageVariableSchema = {
    id: '',
    name: '',
    dsId: '',
    filter: { expression: { where: 'AND', conditions: [] } },
    sorts: [],
    items: []
}

export const VariableForm = ({ index, dataSourceList, defaultValues, onCancel, onConfirm }: VariableFormProps) => {
    const methods = useForm<PageVariableFormSlice>({
        mode: 'onSubmit',
        defaultValues: defaultValues || DEFAULT_VALUES
    })
    const { control, watch, register, setValue, handleSubmit, formState } = methods

    const dsId = watch('dsId')
    const dataSource = useMemo(() => dataSourceList.find(ds => ds.id === dsId), [dataSourceList, dsId])

    return (
        <FormProvider {...methods}>
            <StyledForm onSubmit={handleSubmit(values => onConfirm(values as PageVariableSchema))}>
                <Flex
                    px={32}
                    pb={60}
                    direction="column"
                    gap={16}
                    styles={{
                        root: {
                            overflow: 'auto'
                        }
                    }}
                >
                    <Box>
                        <SC.FormItemHeader>
                            <SC.FormLabel required>标题</SC.FormLabel>
                        </SC.FormItemHeader>
                        <Box py={4}>
                            <Input {...register('name', { required: '标题不能为空' })} size="lg" />
                        </Box>
                        {formState.errors.name?.message && <SC.ErrorMessage>{formState.errors.name?.message}</SC.ErrorMessage>}
                    </Box>

                    <Box>
                        <SC.FormItemHeader>
                            <SC.FormLabel required>想要查询的表</SC.FormLabel>
                        </SC.FormItemHeader>
                        <Box py={4}>
                            <Controller
                                control={control}
                                name="dsId"
                                rules={{ required: '请选择一个数据表' }}
                                render={({ field }) => (
                                    <SelectDataSource
                                        width="100%"
                                        size="lg"
                                        dataSourceList={dataSourceList}
                                        value={field.value}
                                        onChange={v => {
                                            setValue('filter.expression.conditions', [])
                                            setValue('sorts', [])

                                            const ds = dataSourceList.find(item => item.id === v)
                                            if (ds) {
                                                const fields = Object.values(ds.schema).map(field => ({
                                                    id: nanoid(),
                                                    name: field.name,
                                                    fieldId: field.id
                                                }))
                                                setValue('items', fields)
                                            } else {
                                                setValue('items', [])
                                            }
                                            field.onChange(v)
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        {formState.errors.dsId?.message && <SC.ErrorMessage>{formState.errors.dsId?.message}</SC.ErrorMessage>}
                    </Box>

                    {dataSource && (
                        <>
                            <ConditionConfigure variableIndex={index} dataSource={dataSource} dataSourceList={dataSourceList} />

                            <SortConfigure dataSource={dataSource} />

                            <VariableList dataSource={dataSource} />
                        </>
                    )}
                </Flex>

                <Divider />

                <Flex gap={12} justifyContent="flex-end" px={20} py={16}>
                    <Button size="lg" style={{ width: 92, height: 40 }} type="tag" onClick={onCancel}>
                        取消
                    </Button>
                    <Button size="lg" style={{ width: 92, height: 40 }} type="primary" htmlType="submit">
                        确定
                    </Button>
                </Flex>
            </StyledForm>
        </FormProvider>
    )
}
